import React, { useState } from "react";
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  Modal,
} from "react-native";

import Fonts from "../../Resources/Fonts";
import Colors from "../../Resources/Color";
import GlobalStyles from "../../Resources/Styles";

import { useTranslation } from "react-i18next";
import { useScreenDimensions, useDevice } from "../_layout";
import { runInAction } from "mobx";
import NoTranslateText from "./NoTranslateText";
import { useModalKeyboardShortcuts } from "../platform/guards/modalKeyboardShortcuts";

const CustomAlert = ({
  isVisible,
  title,
  text,
  action,
  onClose,
  specialCTA,
  auxText,
  textParams = undefined,
  coach,
  predefinedToDelete,
  attributeToDelete,
  // Optional: when provided, user must check the box to enable Save/Confirm
  doubleConfirmationText = false,
  noTranslateText = false, // New prop to control translation of text
}) => {
  const { t } = useTranslation();
  const { screenSize } = useScreenDimensions();
  const { isMobile } = useDevice();
  const [isDoubleConfirmed, setIsDoubleConfirmed] = useState(false);
  const hasAction = typeof action === "function";

  const predefined =
    coach &&
    coach.customPredefinedExercises &&
    coach.customPredefinedExercises.find((ex) => ex.id === predefinedToDelete);
  const attribute =
    coach &&
    coach.customPredefinedAttributes &&
    coach.customPredefinedAttributes.find(
      (att) => att.id === attributeToDelete
    );
  const isConfirmEnabled = !doubleConfirmationText || isDoubleConfirmed;

  const handleConfirm = async () => {
    if (!hasAction || !isConfirmEnabled) return;

    if (attributeToDelete) {
      coach.updateCoachDeletionAttributes(attributeToDelete);
      const attributeIndex = coach.customPredefinedAttributes.findIndex(
        (attribute) => attribute.id === attributeToDelete
      );
      if (attributeIndex !== -1) {
        runInAction(() => {
          coach.customPredefinedAttributes.splice(attributeIndex, 1);
        });
      }
      coach.startOrResetBatchTimer();
    }
    if (predefinedToDelete) {
      coach.updateCoachDeletionExercises(predefinedToDelete);
      const exerciseIndex = coach.customPredefinedExercises.findIndex(
        (exercise) => exercise.id === predefinedToDelete
      );
      if (exerciseIndex !== -1) {
        runInAction(() => {
          coach.customPredefinedExercises.splice(exerciseIndex, 1);
        });
      }
      coach.startOrResetBatchTimer();
    }
    await action();
    onClose();
  };

  useModalKeyboardShortcuts({
    isVisible,
    onConfirm: hasAction ? handleConfirm : undefined,
    onCancel: onClose,
    isConfirmEnabled,
    closeOnEnterWithoutConfirm: !hasAction,
  });

  const Buttons = ({ bool }) => {
    const text = bool
      ? specialCTA
        ? specialCTA
        : "save-string"
      : "cancel-string";

    const [isHovered, setIsHovered] = useState(false);
    const isDisabled = bool && !!doubleConfirmationText && !isDoubleConfirmed;

    return (
      <TouchableOpacity
        onPress={bool ? handleConfirm : onClose}
        // disabled={isDisabled}
        onMouseEnter={() => !isMobile && setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <View
          style={{
            // marginHorizontal: 20,
            backgroundColor: bool
              ? specialCTA === "delete-string"
                ? "red"
                : Colors.bubbleColor
              : Colors.whiteColor,
            borderRadius: 14,
            borderWidth: 1,
            justifyContent: "center",
            borderColor: bool
              ? specialCTA === "delete-string"
                ? "red"
                : Colors.bubbleColor
              : "red",
            width: 80,
            height: 35,
            justifyContent: "center",
            alignItems: "center",
            opacity: isDisabled ? 0.4 : 1,
          }}
        >
          <Text
            style={{
              textAlignVertical: "center",
              textAlign: "center",
              color: bool ? Colors.whiteColor : "red",
              fontWeight: isHovered ? "500" : "normal",
            }}
          >
            {t(text)}
          </Text>
        </View>
      </TouchableOpacity>
    );
  };
  const OkButton = () => {
    const [isHovered, setIsHovered] = useState(false);

    return (
      <TouchableOpacity
        onPress={onClose}
        onMouseEnter={() => !isMobile && setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <View
          style={{
            marginHorizontal: 20,
            marginTop: 20,
            backgroundColor: Colors.bubbleColor,
            borderRadius: 14,
            borderWidth: 1,
            borderColor: Colors.bubbleColor,
            // width: 80,
            height: 35,
            justifyContent: "center",
            alignItems: "center",
            alignSelf: "center",
            opacity: isHovered ? 0.7 : 1,
          }}
        >
          <Text
            style={{
              paddingHorizontal: 10,
              textAlignVertical: "center",
              textAlign: "center",
              color: Colors.whiteColor,
            }}
          >
            {t("okay-string")}
          </Text>
        </View>
      </TouchableOpacity>
    );
  };
  return (
    <Modal
      animationType="fade"
      transparent={true}
      visible={isVisible}
      onRequestClose={onClose}
    >
      <View style={GlobalStyles.centeredView}>
        <TouchableOpacity
          onPress={onClose}
          style={GlobalStyles.touchableBackground}
        >
          <View />
        </TouchableOpacity>
        <View
          style={{ position: "absolute" }}
          onStartShouldSetResponder={() => false}
        >
          <View
            style={[
              styles.modalView,
              {
                backgroundColor: Colors.whiteColor,
                maxWidth: screenSize === "small" ? 300 : 400,
                padding: 24, // Ensure there is padding to prevent text touching the borders
              },
            ]}
          >
            <View style={{ alignItems: "center" }}>
              <Text style={[Fonts.bold18Centered, { marginBottom: 16 }]}>
                {t(title)}
              </Text>
              {noTranslateText ? (
                <NoTranslateText
                  style={{
                    textAlign: "center",
                    color: Colors.blackColor,
                    width: "100%",
                    flexShrink: 1,
                  }}
                >
                  {predefined
                    ? t("sureToDelete-string", { itemName: t(predefined.name) })
                    : attribute
                    ? t("sureToDelete-string", { itemName: t(attribute.name) })
                    : t(text, textParams)}
                </NoTranslateText>
              ) : (
                <Text
                  style={{
                    textAlign: "center",
                    color: Colors.blackColor,
                    width: "100%",
                    flexShrink: 1,
                  }}
                >
                  {predefined
                    ? t("sureToDelete-string", { itemName: t(predefined.name) })
                    : attribute
                    ? t("sureToDelete-string", { itemName: t(attribute.name) })
                    : t(text, textParams)}
                </Text>
              )}
            </View>
            {auxText && (
              <View style={{ alignItems: "center", flexShrink: 1 }}>
                <Text
                  style={{
                    textAlign: "center",
                    color: Colors.grayColor,
                    fontSize: 11,
                    marginTop: 5,
                  }}
                >
                  {t(auxText)}
                </Text>
              </View>
            )}

            {/* Optional double confirmation checkbox */}
            {action && !!doubleConfirmationText && (
              <TouchableOpacity
                onPress={() => setIsDoubleConfirmed((prev) => !prev)}
                activeOpacity={0.8}
                style={{
                  flexDirection: "row",
                  alignItems: "center",
                  marginTop: 16,
                  alignSelf: "center",
                }}
              >
                <View
                  style={{
                    width: 18,
                    height: 18,
                    borderRadius: 4,
                    borderWidth: 1,
                    borderColor: Colors.grayColor,
                    justifyContent: "center",
                    alignItems: "center",
                    marginRight: 8,
                    backgroundColor: isDoubleConfirmed
                      ? Colors.bubbleColor
                      : Colors.whiteColor,
                  }}
                >
                  {isDoubleConfirmed && (
                    <Text style={{ color: Colors.whiteColor, fontSize: 12 }}>
                      ✓
                    </Text>
                  )}
                </View>
                <Text
                  style={{
                    color: Colors.blackColor,
                    flexShrink: 1,
                    textAlign: "left",
                  }}
                >
                  {t(doubleConfirmationText)}
                </Text>
              </TouchableOpacity>
            )}

            {action ? (
              <View
                style={[
                  GlobalStyles.HStack,
                  {
                    justifyContent: "center",
                    width: "100%",
                    justifyContent: "space-around",
                    marginTop: 20,
                  },
                ]}
              >
                <Buttons bool={false} />
                <Buttons bool={true} />
              </View>
            ) : (
              <View>
                <OkButton />
              </View>
            )}
          </View>
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  centeredView: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center", // Center the modal horizontally
  },

  modalView: {
    // minWidth: 300,
    // maxWidth: "80%",
    alignSelf: "center",
    backgroundColor: Colors.whiteColor,
    borderRadius: 18,
    paddingHorizontal: 24,
    paddingVertical: 24,
    // alignItems: "center",
    // justifyContent: "space-between",
  },

  touchableBackground: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(15, 15, 15, 0.2)",
    justifyContent: "center",
    alignItems: "center",
  },
});

export default CustomAlert;
